
import { TelemetryStatus } from "@/store-services/telemetry.types";
import { Component, Vue, Watch } from "vue-property-decorator";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import dayOfYear from "dayjs/plugin/dayOfYear";
import duration, { Duration } from "dayjs/plugin/duration";
import timezone from "dayjs/plugin/timezone";
import calendar from "dayjs/plugin/calendar";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import AboutComponent from "./about.vue";
import BodyConfigComponent from "./body-config.vue";
import NASATVScheduleComponent from "./nasatv_schedule.vue";
import SpaceStationBlogComponent from "./spacestation_blog.vue";
import HelpComponent from "./help.vue";
import { CountdownInterface } from "../store/config.types";
import HeaderCountdownConfigComponent from "./header-countdown-config.vue";
import { INASATVSchedule, INASATVScheduleEvent } from "../store-services/staticdata.types";

dayjs.extend(utc)
dayjs.extend(dayOfYear)
dayjs.extend(duration)
dayjs.extend(timezone)
dayjs.extend(calendar)
dayjs.extend(LocalizedFormat)

interface INextEventFormatted {
  event: {
    time: Dayjs,
    content: string,
    channel: string
  },
  diff: Duration
}

function formatDateMCC(value: dayjs.Dayjs | duration.Duration) {
  let days = (
    dayjs.isDayjs(value) ?
      value.dayOfYear() :
      ((value.subtract({years: value.years()})).asDays() | 0)
  ).toString().padStart(3, "0");
  return `${days}:${value.format("HH:mm:ss")}`
}
function formatDateGMTText(value: dayjs.Dayjs) {
  return `${value.format("DD.MM.YYYY HH:mm:ss")} GMT`
}
function formatDuration(durationSeconds: number) {
  let sign = durationSeconds < 0 ? "-" : "+"
  let value = dayjs.duration((sign === "-" ? -1 : 1) * durationSeconds)
  return `${sign}${value.hours().toString().padStart(2, "0")}:${value.format("mm:ss")}`
}
function formatDurationDays(durationSeconds: number) {
  let sign = durationSeconds < 0 ? "-" : "+"
  let value = dayjs.duration((sign === "-" ? -1 : 1) * durationSeconds)
  return `${sign}${(value.asDays() | 0).toString().padStart(3, "0")}:${value.hours().toString().padStart(2, "0")}:${value.format("mm:ss")}`
}

@Component({
  components: {
    "cust-header-countdown-config": HeaderCountdownConfigComponent,
    "cust-about": AboutComponent,
    "cust-body-config": BodyConfigComponent,
    "cust-nasatvschedule": NASATVScheduleComponent,
    "cust-spacestationblog": SpaceStationBlogComponent,
    "help": HelpComponent
  }
})
export default class HeaderComponent extends Vue {
  showInfo: boolean = false;
  showSettings: boolean = false;
  showNASATVSchedule: boolean = false;
  showSpaceStationBlog: boolean = false;
  nextEventFormatted: INextEventFormatted = {
    event: {
      time: dayjs(0),
      content: "Error",
      channel: ""
    },
    diff: dayjs.duration(0)
  };
  nextEventPopup: boolean = false;
  contextHelp: string = "## smallwebsites-iss\nThis is a small application to display some stuff about the ISS. It's content ranges from the available live-streams to the publicly available telemetry data and some computed locations and orbits. And the best thing about it: You can customize the layout like you want it and it is saved in your browser. But you can export it. Just check out the menu (click on <i class=\"v-icon notranslate mdi mdi-dots-vertical\"></i>).\n\nFeel free to try it out, you can only break it for yourself! (And theres always an option available to reset the config and if that doesn't work, just delete the data for the website from your browsers menu and startover.)\n\nHave fun and definitly check out the about page to find out where the data is from."

  get fullscreenState() {
    return this.$store.getters.fullscreenState;
  }

  get editModeState() {
    return this.$store.getters.editModeState;
  }

  get telemetryStatus(): TelemetryStatus {
    return this.$store.getters["telemetry/getStatus"]();
  }

  // GMT
  get currentTime() {
    let time = dayjs.utc(this.telemetryStatus.time.current)
    time.set("milliseconds", time.millisecond() % 250)
    return time
  }
  get currentTimeText() {
    return formatDateGMTText(this.currentTime)
  }
  get currentTimeMCC() {
    return formatDateMCC(this.currentTime)
  }

  // ISS MET
  // Calculated ISS MET base: 1998-11-20T06:40:00.000Z
  get isslaunchtime() {
    return dayjs.utc("1998-11-20T06:40:00.000Z");
  }
  get isslaunchtimeText() {
    return formatDateGMTText(this.isslaunchtime)
  }
  get issmet() {
    let met = dayjs.duration(
      this.currentTime.diff(this.isslaunchtime, "seconds"),
      "seconds"
    );

    return `${met.format("Y")} YRS++${formatDateMCC(met)}`;
  }

  // Local Time
  get currentTimeLOCAL() {
    return formatDateMCC(this.currentTime.local())
  }
  // MCC HOU (Mission Control Center Houston) Time
  get currentTimeMCCHOU() {
    return formatDateMCC(this.currentTime.tz("CST6CDT"))
  }
  // MCC MOS (Mission Control Center Moscow) Time
  get currentTimeMCCMOS() {
    return formatDateMCC(this.currentTime.tz("Europe/Moscow"))
  }

  // Next DPC
  get nextDPC() {
    let currentTime = this.currentTime

    return (new Array([0, 7, 30], [0, 18, 30], [1, 7, 30], [1, 18, 30])).map(t => {
      let ts = currentTime.add(t[0], 'day')
      ts = ts.set("hour", t[1])
      ts = ts.set("minute", t[2])
      ts = ts.set("second", 0)
      ts = ts.set("millisecond", 0)
      return {ts, diff: currentTime.diff(ts)}
    }).find(d => d.diff < 0) || {ts: dayjs(0), diff: 0}
  }
  get nextDPCTimeText() {
    return this.nextDPC.ts.calendar()
  }
  get nextDPCDiffText() {
    return formatDuration(this.nextDPC.diff)
  }

  // Crew Wake/ Sleep
  get nextCrewWakeSleep() {
    let currentTime = this.currentTime

    return (new Array(
      {type: "Wake", time: [0, 6, 0]},
      {type: "Sleep", time: [0, 21, 30]},
      {type: "Wake", time: [1, 6, 0]},
      {type: "Sleep", time: [1, 21, 30]})
    ).map(t => {
      let ts = currentTime.add(t.time[0], 'day')
      ts = ts.set("hour", t.time[1])
      ts = ts.set("minute", t.time[2])
      ts = ts.set("second", 0)
      ts = ts.set("millisecond", 0)
      return {type: t.type, ts, diff: currentTime.diff(ts)}
    }).find(d => d.diff < 0) || {type: "Unknown", ts: dayjs(0), diff: 0}
  }
  get nextCrewWakeSleepTypeText() {
    return this.nextCrewWakeSleep.type.toUpperCase()
  }
  get nextCrewWakeSleepTimeText() {
    return `${this.nextCrewWakeSleep.type}-Period is ${this.nextCrewWakeSleep.ts.calendar()}`
  }
  get nextCrewWakeSleepDiffText() {
    return formatDuration(this.nextCrewWakeSleep.diff)
  }

  // Next event (NASA TV schedule) popup
  get nextEventPopupEnabled() {
    return this.$store.getters["config/getNextEventPopup"]()
  }
  get nextEvent(): INASATVScheduleEvent {
    if (this.nextEventPopupEnabled) {
      let time = this.currentTime
      let schedule = this.$store.getters["staticdata/getNASATVSchedule"] as INASATVSchedule
      
      if ((time.diff(dayjs(schedule.timestamp)) > dayjs.duration({minutes: 30}).asMilliseconds()) && (schedule.timestamp.getTime() === 0 || schedule.status !== "loading")) {
        this.$store.dispatch("staticdata/updateNASATVSchedule")
      } else if (schedule.status === "ok" || (schedule.status === "loading" && schedule.content?.data)) {
        let nextEvent = schedule.content?.data.schedule.find(e => dayjs(e.time).diff() < dayjs.duration({minutes: 5}).asMilliseconds() && dayjs(e.time).diff() > 0)
        if (nextEvent) {
          return nextEvent
        }
      }
    }

    return {} as INASATVScheduleEvent
  }

  // Countdowns
  get countdowns() {
    let currentTime = this.currentTime.utc()
    let countdowns = (this.$store.getters["config/getCountdown"]() as Array<CountdownInterface>)
      .map((countdown, i) => {
        return {
          id: i,
          enabled: countdown.enabled,
          title: countdown.title,
          description: `${countdown.description !== "" ? countdown.description + ": " : "" }` +
            (countdown.timestamp === undefined ? `Paused` : `Targeting ${formatDateGMTText(dayjs(countdown.timestamp))}`),
          target: countdown.remainingSeconds ? formatDurationDays(countdown.remainingSeconds) : 
          formatDurationDays(currentTime.diff(dayjs.utc(countdown.timestamp)))
        }
      }).filter(c => c.enabled).slice(0, 3)
    return countdowns
  }
  pauseCountdown(i: number) {
    let countdowns = (this.$store.getters["config/getCountdown"]() as Array<CountdownInterface>)
    let countdown = countdowns[i]
    let time = this.currentTime.utc().clone().set('milliseconds', 0)
    if (countdown.timestamp === undefined) {
      countdown.timestamp = time.subtract(countdowns[i].remainingSeconds || 0, "milliseconds").toISOString()
      countdown.remainingSeconds = undefined
    } else {
      countdown.remainingSeconds = time.diff(dayjs.utc(countdowns[i].timestamp))
      countdown.timestamp = undefined
    }
    Vue.set(countdowns, i, countdown)
    this.$store.dispatch("config/updateCountdowns", countdowns)
  }

  @Watch("nextEvent")
  nextEventWatch(newValue: INASATVScheduleEvent, oldValue: INASATVScheduleEvent) {
    if (!oldValue.hasOwnProperty("time") && newValue.hasOwnProperty("time")) {
      this.nextEventPopup = true;
    } else if (oldValue.hasOwnProperty("time") && !newValue.hasOwnProperty("time")) {
      this.nextEventPopup = false;
    }

    if (this.nextEventPopup && newValue.hasOwnProperty("time")) {
      this.nextEventFormatted = {
        event: {
          ...newValue,
          time: dayjs(newValue.time),
          content: newValue.content.replaceAll(/\[([^\]]*)\]\(([^\)]*)\)/g, (match, text, href) => `<a href="${href}" target="_blank">${text}</a>`)
          .replaceAll(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/g, (match) => dayjs(match).isSame(dayjs(), "date") ? dayjs(match).format("LTS") : dayjs(match).format("LTS[ on ]ll"))
        },
        diff: dayjs.duration(Math.abs(dayjs(newValue.time).diff()))
      }
    }
  }
}
